import {
  Component,
  ElementRef,
  effect,
  input,
  output,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

@Component({
    selector: 'app-popover',
    imports: [MatIconModule],
    template: `
    <div
      #popover
      popover="auto"
      class="popover"
      [style.--popover-width]="width()"
      [style.--popover-max-width]="width()"
    >
      <button 
        class="close-button" 
        (click)="handleCloseClick()"
        i18n-aria-label="@@lib.popover.close"
        aria-label="Schließen"
      >
        <mat-icon>close</mat-icon>
      </button>
      <div class="popover-content">
        <ng-content></ng-content>
      </div>
    </div>
    <div
      #backdrop
      class="backdrop"
      [attr.open]="isOpen() ? '' : null"
      (click)="handleBackdropClick()"
    ></div>
  `,
    changeDetection: ChangeDetectionStrategy.Eager,
    styles: [
        `
      .popover {
        background: white;
        border-radius: 12px;
        border: none;
        padding: 0;
        width: var(--popover-width, auto);
        max-width: min(var(--popover-max-width, 600px), calc(100vw - 48px));
        min-width: min(300px, calc(100vw - 48px));
        max-height: calc(100vh - 48px);
        overflow-y: auto;
        transition: opacity 0s ease-in-out, transform 0.3s ease-in-out;
        opacity: 0;
        transform: scale(0.9);
        position: fixed;
        z-index: 1001;
      }

      .popover:popover-open {
        opacity: 1;
        transform: scale(1);
      }

      @media (max-width: 600px) {
        .popover {
          margin-top: 50px;
          width: auto;
          max-width: calc(90vw);
          max-height: calc(100vh - 32px);
        }
      }

      .close-button {
        position: absolute;
        top: 0px;
        right: 0px;
        border: none;
        background: rgba(255, 255, 255, 0.9);
        cursor: pointer;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 0 12px 0 50%;
        color: #666;
        z-index: 1002;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        transition: all 0.2s ease;
        
        &:hover {
          background: rgba(255, 255, 255, 1);
          color: #333;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }

        mat-icon {
          width: 20px;
          height: 20px;
          font-size: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }

      .backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.3);
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.3s ease-in-out;
        z-index: 1000;
      }

      .backdrop[open] {
        opacity: 1;
        pointer-events: auto;
      }

      @media (prefers-reduced-motion: reduce) {
        .popover,
        .backdrop {
          transition: none;
        }
      }
    `,
    ]
})
export class PopoverComponent {
  popover = viewChild<ElementRef<HTMLElement>>('popover');

  isOpen = input<boolean>(false);
  /** Optional fixed modal width (e.g. '680px'). Unset → content-sized, capped at 600px (default). */
  width = input<string | null>(null);
  close = output<void>();

  constructor() {
    effect(() => {
      const popoverEl = this.popover()?.nativeElement;
      if (!popoverEl) return;

      if (!this.isOpen()) {
        popoverEl.hidePopover();
      } else {
        popoverEl.showPopover();
      }
    });
  }

  protected handleBackdropClick(): void {
    if (this.isOpen()) {
      this.close.emit();
    }
  }

  protected handleCloseClick(): void {
    this.close.emit();
  }
}
